import React from 'react';
import { useSearchParams } from 'react-router-dom';
import { MobileHeader, Card, Alert } from '@parsina/framework';
import { Sparkles } from 'lucide-react';

import { C, CT } from '../../constants/theme';
import { useTranslation } from '../../i18n/I18nProvider.jsx';
import { useMe } from '../../hooks/useMe.js';
import { useWallet } from '../../hooks/useWallet.js';
import { fmtParseh } from '../../constants/pricing';
import { useCreditPrices } from '../../lib/useCreditPrices.js';
import { DEFAULT_PAGE_SIZE, clampPage } from '../../lib/paging.js';
import { CostChip } from '../../components/common/ParsehCost.jsx';
import { MobileSectionTitle } from '../../components/mobile/MobileKit.jsx';
import TopUpCard from './TopUpCard.jsx';
import GiftCodeCard from './GiftCodeCard.jsx';
import TransactionList from './TransactionList.jsx';

// پارسه — the phone wallet, a top-level bottom-nav destination.
//
// ┌──────────────────────────────────────┐
// │ ① MobileHeader — cockpit + balance    │  the number rides the gradient
// │ ② the free-grant note                 │
// │ ③ شارژ                                │
// │ ④ کد هدیه                             │
// │ ⑤ price list                          │
// │ ⑥ transactions (compact)              │
// │ [ BottomNav ]                          │
// └──────────────────────────────────────┘
//
// The desktop's two columns become one, and the order changes with it: on a
// phone, the reason a student opened this screen is almost always «شارژ», so it
// comes before the ledger rather than beside it. The transaction table drops its
// description column — four columns do not survive 320px without the page
// scrolling sideways — and the description is not the column a student scans for.

export default function CreditsMobile() {
  const { t, formatNumber } = useTranslation();
  // The policy's figures once the list has arrived; the shipped constants until then.
  const { priceList } = useCreditPrices();
  const { me, balance, loading } = useMe();
  const [searchParams, setSearchParams] = useSearchParams();

  const filter = searchParams.get('type') || null;
  const urlPage = Number(searchParams.get('page')) || 1;

  const { ledger, gifts, topUp, refreshBalance } = useWallet({
    page: urlPage,
    pageSize: DEFAULT_PAGE_SIZE,
    type: filter,
  });

  // Returning from the bank — see the desktop surface for why.
  React.useEffect(() => {
    refreshBalance();
  }, [refreshBalance]);

  const page = clampPage(urlPage, ledger.totalCount, DEFAULT_PAGE_SIZE);

  const setFilter = (value) => {
    const params = new URLSearchParams(searchParams);
    if (value) params.set('type', value);
    else params.delete('type');
    params.delete('page');
    setSearchParams(params, { replace: true });
  };

  const setPage = (next) => {
    const params = new URLSearchParams(searchParams);
    if (next <= 1) params.delete('page');
    else params.set('page', String(next));
    setSearchParams(params);
  };

  return (
    <div style={{ flex: 1, minHeight: 0, display: 'flex', flexDirection: 'column' }}>
      <div style={{ flex: 1, minHeight: 0, overflowY: 'auto' }} data-testid="credits-scroll">
        <MobileHeader
          title={t('credits.title')}
          subtitle={t('credits.sub')}
          user={{ name: me?.displayName || '' }}
          greeting={t('mobile.greeting')}
        >
          {/* The white-alpha inset card that rides the gradient — the same shape
              the kit's DateNavigator uses in `onGradient` mode. */}
          <div
            style={{
              background: 'rgba(255,255,255,0.14)',
              borderRadius: 14,
              padding: '0.75rem 1rem',
              color: '#fff',
            }}
            data-testid="balance-hero"
          >
            <div style={{ fontSize: '0.6875rem', color: 'rgba(255,255,255,0.7)', fontWeight: 700 }}>
              {t('credits.balance.label')}
            </div>
            <div style={{ display: 'flex', alignItems: 'flex-end', gap: 6, marginTop: 4 }}>
              <span style={{ fontSize: '1.75rem', fontWeight: 800, lineHeight: 1 }} dir="ltr">
                {/* «—» while the read is in flight, never «۰». */}
                {loading && balance == null ? t('common.labels.none') : fmtParseh(balance, formatNumber)}
              </span>
              <span style={{ fontSize: '0.8125rem', color: 'rgba(255,255,255,0.75)' }}>
                {t('common.units.parseh')}
              </span>
            </div>
          </div>
        </MobileHeader>

        <div style={{ padding: '0.875rem 1rem 1.5rem', display: 'flex', flexDirection: 'column', gap: '0.875rem' }}>
          <Alert
            variant="success"
            title={t('credits.free.title')}
            icon={<Sparkles size={18} aria-hidden />}
          >
            {t('credits.free.body')}
          </Alert>

          <TopUpCard topUp={topUp} />

          <GiftCodeCard gifts={gifts} onRedeemed={() => ledger.reload()} />

          <div>
            <MobileSectionTitle>{t('credits.priceList.title')}</MobileSectionTitle>
            <Card>
              <div style={{ display: 'flex', flexDirection: 'column' }}>
                {priceList.map((row, index) => (
                  <div
                    key={row.id}
                    style={{
                      display: 'flex',
                      alignItems: 'center',
                      gap: 8,
                      padding: '8px 0',
                      borderBottom: index < priceList.length - 1 ? `1px solid ${C.border}` : 'none',
                    }}
                  >
                    <span style={{ flex: 1, fontSize: '0.8125rem', color: CT.ink2 }}>
                      {t(row.labelKey)}
                    </span>
                    <CostChip cost={row.cost} />
                  </div>
                ))}
              </div>
            </Card>
          </div>

          <TransactionList
            ledger={ledger}
            filter={filter}
            onFilter={setFilter}
            page={page}
            onPage={setPage}
            compact
          />
        </div>
      </div>
    </div>
  );
}
